import { useState, useRef, useEffect } from "react";
import ProctorCamera from "../../components/ProctorCamera";
import PreCheckCamera from "../../components/PreCheckCamera";

export default function ProctoredTest({ onAutoSubmit }) {
  const WARNING_LIMIT = 5;

  // 🔹 FLOW
  const [step, setStep] = useState("idle"); 
  // idle | consent | face | running

  const [testStarted, setTestStarted] = useState(false);
  const [useProctor, setUseProctor] = useState(false);
  const [proctorEnabled, setProctorEnabled] = useState(false);

  // 🔹 PROCTOR STATES
  const [status, setStatus] = useState("Proctoring Not Started");
  const [warnings, setWarnings] = useState([]);
  const [lastReason, setLastReason] = useState(null);
  const [showReason, setShowReason] = useState(false);
  const [preCheckOk, setPreCheckOk] = useState(false);

  const hideTimer = useRef(null);

  // -------------------------------
  // CHEATING REASON LOGIC
  // -------------------------------
  const getCheatingReasons = (data) => {
    const reasons = [];

    if (data.faces === 0)
      reasons.push("❌ Face not visible in camera");

    if (data.faces > 1)
      reasons.push("❌ Multiple people detected");

    if (data.eye_state && data.eye_state !== "center")
      reasons.push("❌ Looking away from screen");

    if (data.head_suspicious > 0)
      reasons.push("❌ Suspicious head movement");

    return reasons;
  };

  // -------------------------------
  // HANDLE CHEATING
  // -------------------------------
  const handleCheating = (data) => {
    const reasons = getCheatingReasons(data);
    if (!reasons.length) return;

    const combined = reasons.join(", ");
    if (combined === lastReason) return;

    setWarnings((prev) => {
      const updated = [...prev, { time: Date.now(), reasons }];

      if (updated.length >= WARNING_LIMIT) {
        alert("🚫 Too many warnings. Test auto-submitted.");
        setProctorEnabled(false);
        onAutoSubmit();
      }

      setStatus(`⚠️ Warning ${updated.length}/${WARNING_LIMIT}`);
      return updated;
    });

    setLastReason(combined);
    setShowReason(true);

    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setShowReason(false), 3000);
  };

  useEffect(() => {
    return () => hideTimer.current && clearTimeout(hideTimer.current);
  }, []);

  // -------------------------------
  // RENDER
  // -------------------------------
  return (
    <>
      {/* 🔹 SMALL START TEST BUTTON (ALWAYS VISIBLE) */}
      {!testStarted && (
        <div className="fixed left-4 top-20 z-40">
          <button
            onClick={() => setStep("consent")}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            ▶ Start Test
          </button>
        </div>
      )}

      {/* 🔹 TEST UI (LOCKED UNTIL START) */}
      <div className={`${!testStarted ? "opacity-50 pointer-events-none" : ""}`}>
        {/* 👉 YAHAN TUMHARA ACTUAL TEST UI RAHEGA */}
      </div>

      {/* 🔹 CONSENT MODAL */}
      {step === "consent" && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-xl w-[90%] max-w-md">
            <h2 className="text-lg font-bold mb-2">
              Enable Proctor AI?
            </h2>

            <p className="text-sm text-gray-600 mb-4">
              Proctor AI will monitor face, eyes and head movement.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setUseProctor(true);
                  setStep("face");
                }}
                className="flex-1 bg-green-600 text-white py-2 rounded"
              >
                Yes
              </button>

              <button
                onClick={() => {
                  setUseProctor(false);
                  setTestStarted(true);
                  setStep("running");
                }}
                className="flex-1 bg-gray-400 text-white py-2 rounded"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔹 FACE VERIFICATION */}
      {step === "face" && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-xl w-[90%] max-w-md">
            <h2 className="text-lg font-bold mb-2">
              Face Verification
            </h2>

            <PreCheckCamera onReady={setPreCheckOk} />

            <button
              disabled={!preCheckOk}
              onClick={() => {
                setProctorEnabled(true);
                setTestStarted(true);
                setStatus("Monitoring Enabled");
                setStep("running");
              }}
              className={`mt-4 w-full py-2 rounded ${
                preCheckOk
                  ? "bg-green-600 text-white"
                  : "bg-gray-300 cursor-not-allowed"
              }`}
            >
              Start Test
            </button>
          </div>
        </div>
      )}

      {/* 🔹 PROCTOR STATUS */}
      {testStarted && useProctor && (
        <div className="fixed top-16 right-4 z-40 bg-white shadow px-3 py-2 rounded text-sm font-semibold">
          🛡 {status}
          <br />
          <span className="text-xs text-red-600">
            Warnings: {warnings.length}/{WARNING_LIMIT}
          </span>

          {showReason && lastReason && (
            <div className="mt-1 text-xs text-red-700 bg-red-100 px-2 py-1 rounded">
              {lastReason}
            </div>
          )}
        </div>
      )}

      {/* 🔹 PROCTOR CAMERA */}
      {testStarted && useProctor && proctorEnabled && (
        <ProctorCamera onCheating={handleCheating} />
      )}
    </>
  );
}
